import React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { 
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
  FireIcon,
  StarIcon,
  TrophyIcon,
  GiftIcon
} from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { RootState } from '../../store'
import { useTheme, useAuth } from '../../hooks'
import { formatNumber } from '../../utils/helpers'

interface HeaderProps {
  className?: string
}

const Header: React.FC<HeaderProps> = ({ className = '' }) => {
  const authState = useSelector((state: RootState) => state.auth)
  const { user } = authState
  const { notifications } = useSelector((state: RootState) => state.ui)
  const { 
    theme, 
    toggleSidebarOpen, 
    changeTheme 
  } = useTheme()
  const { logout } = useAuth()



  const unreadNotifications = notifications.filter(n => !n.read).length

  const themeOptions = [
    { value: 'light', label: 'Açık Tema', icon: SunIcon },
    { value: 'dark', label: 'Koyu Tema', icon: MoonIcon },
    { value: 'system', label: 'Sistem', icon: ComputerDesktopIcon }
  ]

  const handleLogout = () => {
    logout()
  }

  return (
    <header className={`
      sticky top-0 z-30 flex items-center justify-between h-16 px-4 lg:px-6
      bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl
      border-b border-gray-200/50 dark:border-gray-700/50
      ${className}
    `}>
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        {/* Mobile Menu Toggle */}
        <button
          onClick={toggleSidebarOpen}
          className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Search */}
        <div className="hidden lg:flex relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Ara..."
            className="
              block w-60 xl:w-80 pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700
              rounded-xl bg-gray-50 dark:bg-gray-800
              text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              transition-all duration-200
            "
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* User Stats */}
        {user && (
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-4 px-2 xl:px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl max-w-fit overflow-hidden">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                <StarIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Seviye</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{user.level}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
                <FireIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">XP</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{formatNumber(user.xp)}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                <TrophyIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Rozet</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{user.badges?.length || 0}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg flex items-center justify-center">
                <GiftIcon className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Coin</p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">{formatNumber(user.coins || 0)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Theme Selector */}
        <Menu as="div" className="relative">
          <Menu.Button className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
            {theme === 'light' && <SunIcon className="w-6 h-6" />}
            {theme === 'dark' && <MoonIcon className="w-6 h-6" />}
            {theme === 'system' && <ComputerDesktopIcon className="w-6 h-6" />}
          </Menu.Button>
          
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                {themeOptions.map((option) => (
                  <Menu.Item key={option.value}>
                    {({ active }) => (
                      <button
                        onClick={() => changeTheme(option.value as any)}
                        className={`
                          ${active ? 'bg-gray-100 dark:bg-gray-700' : ''}
                          ${theme === option.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'}
                          group flex items-center w-full px-4 py-2 text-sm
                        `}
                      >
                        <option.icon className="mr-3 h-5 w-5" />
                        {option.label}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </Menu>

        {/* Notifications */}
        <button className="relative p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
          <BellIcon className="w-6 h-6" />
          {unreadNotifications > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full animate-pulse">
              {unreadNotifications > 99 ? '99+' : unreadNotifications}
            </span>
          )}
        </button>

        {/* User Menu */}
        {user && (
          <Menu as="div" className="relative">
            <Menu.Button className="flex items-center space-x-3 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-sm font-bold text-white">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user.email}
                </p>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            </Menu.Button>

            <Transition
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                    <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                      <span>Seviye {user.level}</span>
                      <span>{formatNumber(user.xp)} XP</span>
                      <span>{formatNumber(user.coins || 0)} Coin</span>
                    </div>
                  </div>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`
                          ${active ? 'bg-gray-100 dark:bg-gray-700' : ''}
                          group flex items-center px-4 py-2 text-sm text-gray-900 dark:text-white
                        `}
                      >
                        <UserCircleIcon className="mr-3 h-5 w-5" />
                        Profil
                      </Link>
                    )}
                  </Menu.Item>
                  
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={`
                          ${active ? 'bg-gray-100 dark:bg-gray-700' : ''}
                          group flex items-center px-4 py-2 text-sm text-gray-900 dark:text-white
                        `}
                      >
                        <Cog6ToothIcon className="mr-3 h-5 w-5" />
                        Ayarlar
                      </Link>
                    )}
                  </Menu.Item>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`
                            ${active ? 'bg-gray-100 dark:bg-gray-700' : ''}
                            group flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400
                          `}
                        >
                          <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                          Çıkış Yap
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    </header>
  )
}

export default Header